import { baseApi } from "@/shared/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type {
  CreateResignationLetterDto,
  ResignationLetterResponse,
  UpdateResignationLetterDto,
  CancelResponse,
  ResignationApproveResponse,
  ResignationRejectResponse,
  ResignationRevisionResponse,
} from "../types";

export const resignationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getResignationLetters: builder.query<ResignationLetterResponse[], void>({
      query: () => ({
        url: "/api/hr/resignation-letters/",
        method: "GET",
        credentials: "include",
      }),
      providesTags: [{ type: "ResignationLetters" as const, id: "LIST" }],
    }),

    getResignationLetter: builder.query<ResignationLetterResponse, number>({
      query: (id) => ({
        url: `/api/hr/resignation-letters/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "ResignationLetters" as const, id }],
    }),

    createResignationLetter: builder.mutation<ResignationLetterResponse, CreateResignationLetterDto>({
      query: (body) => ({
        url: "/api/hr/resignation-letters/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "NGOWorkers" as const, id: "LIST" },
        { type: "ResignationLetters" as const, id: "LIST" },
        { type: "SelfService" as const, id: "REQUESTS" },
      ],
    }),

    updateResignationLetter: builder.mutation<
      ResignationLetterResponse,
      { id: number; data: UpdateResignationLetterDto }
    >({
      query: ({ id, data }) => ({
        url: `/api/hr/resignation-letters/${id}/`,
        method: "PATCH",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ResignationLetters" as const, id },
        { type: "ResignationLetters" as const, id: "LIST" },
      ],
    }),

    deleteResignationLetter: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/hr/resignation-letters/${id}/`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "ResignationLetters" as const, id: "LIST" },
        { type: "NGOWorkers" as const, id: "LIST" },
      ],
    }),

    cancelResignationLetter: builder.mutation<CancelResponse, number>({
      query: (id) => ({
        url: `/api/hr/resignation-letters/${id}/cancel/`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ResignationLetters" as const, id },
        { type: "ResignationLetters" as const, id: "LIST" },
      ],
    }),

    approveResignation: builder.mutation<
      ResignationApproveResponse,
      { id: number; cms_signature: string; file_base64: string }
    >({
      query({ id, cms_signature, file_base64 }) {
        return {
          url: `/api/hr/resignation-letters/${id}/approve/`,
          method: "POST",
          body: { cms_signature, file_base64 },
          credentials: "include",
        };
      },
      invalidatesTags(_result, _error, { id }) {
        return [
          { type: "ResignationLetters" as const, id },
          { type: "ResignationLetters" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
          "PendingActions",
        ];
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "approve_application" && item.type === "resignation_letter",
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
          dispatch(
            resignationApi.util.updateQueryData("getResignationLetter", id, (draft) => {
              draft.status = "signing_pending";
            }),
          );
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    rejectResignation: builder.mutation<ResignationRejectResponse, { id: number; note?: string }>({
      query({ id, note }) {
        return {
          url: `/api/hr/resignation-letters/${id}/reject/`,
          method: "POST",
          body: note ? { note } : {},
          credentials: "include",
        };
      },
      invalidatesTags(_result, _error, { id }) {
        return [
          { type: "ResignationLetters" as const, id },
          { type: "ResignationLetters" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
          "PendingActions",
        ];
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "approve_application" && item.type === "resignation_letter",
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    requestRevisionResignation: builder.mutation<ResignationRevisionResponse, { id: number; note: string }>({
      query({ id, note }) {
        return {
          url: `/api/hr/resignation-letters/${id}/request-revision/`,
          method: "POST",
          body: { note },
          credentials: "include",
        };
      },
      invalidatesTags(_result, _error, { id }) {
        return [
          { type: "ResignationLetters" as const, id },
          { type: "ResignationLetters" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
          "PendingActions",
        ];
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "approve_application" && item.type === "resignation_letter",
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    revokeResignation: builder.mutation<void, number>({
      query(id) {
        return {
          url: `/api/hr/resignation-letters/${id}/revoke/`,
          method: "POST",
          credentials: "include",
        };
      },
      invalidatesTags(_result, _error, id) {
        return [
          { type: "ResignationLetters" as const, id },
          { type: "ResignationLetters" as const, id: "LIST" },
          "PendingActions",
        ];
      },
    }),
  }),
});

export const {
  useGetResignationLettersQuery,
  useGetResignationLetterQuery,
  useLazyGetResignationLetterQuery,
  useCreateResignationLetterMutation,
  useUpdateResignationLetterMutation,
  useDeleteResignationLetterMutation,
  useCancelResignationLetterMutation,
  useApproveResignationMutation,
  useRejectResignationMutation,
  useRequestRevisionResignationMutation,
  useRevokeResignationMutation,
} = resignationApi;
